import { Schedule } from '@medplum/fhirtypes';
import { Document, useMedplum } from '@medplum/react';
import { Box, Button, Card, Container, Grid, Group, Stack, Text, Title, Image, SimpleGrid } from '@mantine/core';
import { Calendar, DatePicker } from '@mantine/dates';
import { useNavigate, useParams } from 'react-router-dom';
import { useState, type JSX } from 'react';

const sessionTypes = [
  {
    id: 'initial-intake',
    title: 'Initial Intake Session',
    duration: '50 minutes',
    description: 'First-time consultation to discuss your needs and goals',
    price: '$150',
  },
  {
    id: 'individual-therapy',
    title: 'Individual Psychotherapy, Standard Session',
    duration: '50 minutes',
    description: 'One-on-one therapy session',
    price: '$130',
  },
  {
    id: 'couples-therapy',
    title: 'Couples / Family Therapy',
    duration: '50 minutes',
    description: 'Therapy session for couples or family members',
    price: '$150',
  },
  {
    id: 'consultation',
    title: 'Consultation / Coaching Services',
    duration: '50 minutes',
    description: 'Professional consultation or coaching session',
    price: '$130',
  },
];

const locations = [
  {
    id: 'online',
    name: 'Online Services',
    address: '1 H Street #203',
    city: 'San Rafael',
    state: 'CA',
    zip: '94901-1750',
    phone: '(415) 545-8899',
    mapUrl: `https://maps.googleapis.com/maps/api/staticmap?center=San+Rafael,CA&zoom=15&size=400x200&key=YOUR_API_KEY`,
  },
];

// Mock available time slots grouped by time of day
const mockTimeSlots = {
  morning: [
    '8:00 AM',
    '9:00 AM',
    '10:00 AM',
    '11:00 AM',
  ],
  afternoon: [
    '1:00 PM',
    '2:00 PM',
    '3:00 PM',
  ],
  evening: [
    '4:00 PM',
    '5:00 PM',
    '6:00 PM',
    '7:00 PM',
  ],
};

export function GetCare(): JSX.Element {
  const navigate = useNavigate();

  return (
    <Container size="lg" py="xl">
      <Title order={1} mb="xl">Schedule a Session</Title>
      
      <Stack gap="md">
        {sessionTypes.map((session) => (
          <Card key={session.id} withBorder shadow="sm" p="lg">
            <Group justify="apart" mb="xs">
              <div>
                <Text fw={500} size="lg">{session.title}</Text>
                <Text size="sm" c="dimmed">{session.duration}</Text>
              </div>
              <Text fw={500}>{session.price}</Text>
            </Group>
            
            <Text size="sm" mb="md">{session.description}</Text>
            
            <Button 
              variant="light"
              onClick={() => navigate(`/get-care/location/${session.id}`)}
            >
              Select & Continue
            </Button>
          </Card>
        ))}
      </Stack>
    </Container>
  );
}

export function LocationSelection(): JSX.Element {
  const navigate = useNavigate();
  const { sessionType } = useParams();
  const selectedSession = sessionTypes.find(s => s.id === sessionType);

  return (
    <Container size="lg" py="xl">
      <Title order={1} mb="md">Select Location</Title>
      <Text c="dimmed" mb="xl">
        {selectedSession?.title} - {selectedSession?.duration}
      </Text>

      <Stack gap="md">
        {locations.map((location) => (
          <Card key={location.id} withBorder shadow="sm" p="lg">
            <Group align="flex-start">
              <Box style={{ flex: 1 }}>
                <Text fw={500} size="lg" mb="xs">{location.name}</Text>
                <Text size="sm">
                  {location.address}<br />
                  {location.city}, {location.state} {location.zip}
                </Text>
                <Text size="sm" mt="xs">{location.phone}</Text>
              </Box>
              <Box>
                <Image
                  src={location.mapUrl}
                  alt={`Map of ${location.name}`}
                  width={400}
                  height={200}
                  radius="md"
                />
              </Box>
            </Group>
            <Button 
              variant="light"
              mt="md"
              onClick={() => navigate(`/get-care/schedule/${sessionType}/${location.id}`)}
            >
              Select & Schedule
            </Button>
          </Card>
        ))}
      </Stack>
    </Container>
  );
}

export function SessionScheduler(): JSX.Element {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const { sessionType, locationId } = useParams();
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const selectedSession = sessionTypes.find(s => s.id === sessionType);
  const selectedLocation = locations.find(l => l.id === locationId);

  const TimeSlotGroup = ({ title, slots }: { title: string; slots: string[] }) => (
    <Card withBorder p="md" h="100%">
      <Text fw={500} size="sm" tt="uppercase" c="dimmed" mb="md">{title}</Text>
      <Stack gap="xs">
        {slots.map((time) => (
          <Button
            key={time}
            fullWidth
            variant={selectedTime === time ? 'filled' : 'light'}
            onClick={() => setSelectedTime(time)}
          >
            {time}
          </Button>
        ))}
      </Stack>
    </Card>
  );

  return (
    <Container size="lg" py="xl">
      <Title order={1} mb="md">Schedule Appointment</Title>
      <Text c="dimmed" mb="xl">
        {selectedSession?.title} at {selectedLocation?.name}
      </Text>

      <Grid>
        <Grid.Col span={6}>
          <Card withBorder p="md">
            <DatePicker
              size="lg"
              value={selectedDate}
              onChange={(value) => setSelectedDate(value ? new Date(value) : null)}
              minDate={new Date()}
              w="100%"
              mb={10}
            />
          </Card>
        </Grid.Col>
        <Grid.Col span={6}>
          <Card withBorder p="md">
            <Title order={3} mb="md">
              {selectedDate ? (
                <>Available Times for {selectedDate.toLocaleDateString()}</>
              ) : (
                'Select a Date'
              )}
            </Title>
            {selectedDate && (
              <>
                <SimpleGrid cols={3} spacing="md">
                  <TimeSlotGroup title="Morning" slots={mockTimeSlots.morning} />
                  <TimeSlotGroup title="Afternoon" slots={mockTimeSlots.afternoon} />
                  <TimeSlotGroup title="Evening" slots={mockTimeSlots.evening} />
                </SimpleGrid>
                
                {selectedTime && (
                  <Button 
                    color="teal" 
                    fullWidth 
                    mt="xl"
                    onClick={() => navigate('/appointments/confirmation')}
                  >
                    Confirm {selectedTime} Appointment
                  </Button>
                )}
              </>
            )}
          </Card>
        </Grid.Col>
      </Grid>
    </Container>
  );
}